import * as React from "react";
import type {
  CompanyCapexForecastPath,
  CompanyCapexSliceResult,
} from "../../models/companyCapex";

export interface CompanyCapexPanelProps {
  result: CompanyCapexSliceResult;
  mode: "monitor" | "forecast";
}

function money(value: number) {
  return `$${value.toFixed(1)}B`;
}

function pctChange(current: number, prior: number) {
  const value = (current / Math.max(prior, 1e-9) - 1) * 100;
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function expertLabel(expert: CompanyCapexForecastPath["selectedExpert"]) {
  if (expert === "bottom-up") return "公司加总";
  if (expert === "lag1") return "lag-1";
  if (expert === "drift") return "4Q drift";
  return "ridge";
}

function Disclosure({ result }: { result: CompanyCapexSliceResult }) {
  return (
    <details className="model-disclosure">
      <summary>方法与来源</summary>
      <div className="method-grid">
        <div><span>目标</span><p>{result.methodology.target}</p></div>
        <div><span>验证</span><p>{result.methodology.validation}</p></div>
        <div><span>模型晋级</span><p>{result.methodology.selection}</p></div>
        <div><span>区间</span><p>{result.methodology.interval}</p></div>
        <div><span>多步预测</span><p>{result.methodology.multiStep}</p></div>
        <div><span>Vintage</span><p>{result.methodology.vintagePolicy}</p></div>
      </div>
      <ul>
        {result.sources.map((source) => (
          <li key={source.id}>
            <a href={source.url} target="_blank" rel="noreferrer">{source.label}</a>
            {` · ${source.tier} · ${source.cadence}`}
          </li>
        ))}
      </ul>
    </details>
  );
}

function MonitorView({ result }: { result: CompanyCapexSliceResult }) {
  const monitor = result.monitor;
  return (
    <section className="sheet company-capex-panel" aria-labelledby="company-capex-monitor-heading">
      <div className="sheet-heading">
        <div>
          <div className="section-kicker">COMPANY CAPEX / MONITOR</div>
          <h2 id="company-capex-monitor-heading">大厂 CapEx 最新监控</h2>
          <p>所有公司对齐到相同 SEC 披露季度，避免把错位数据相加。</p>
        </div>
        <div className="forecast-summary">
          <span>最新共同数据期</span>
          <strong>{monitor.commonPeriod}</strong>
          <small>{monitor.companies.length} 家合计 {money(monitor.totalCapex)}</small>
        </div>
      </div>
      <div className="table-shell">
        <table className="research-table company-table">
          <caption className="sr-only">公司 CapEx 最新实际和 SEC 数据新鲜度</caption>
          <thead>
            <tr><th>公司</th><th>最新实际</th><th>收入</th><th>披露日期</th><th>数据新鲜度</th></tr>
          </thead>
          <tbody>
            {monitor.companies.map((company) => (
              <tr key={company.id}>
                <td><strong>{company.name}</strong><small>{company.id.toUpperCase()}</small></td>
                <td>{money(company.capex)}</td>
                <td>{money(company.revenue)}</td>
                <td>{company.filingDate}</td>
                <td>{company.ageDays} 天</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="data-caveat">
        {monitor.source.label} · {monitor.source.vintagePolicy} · 数据截至 {monitor.asOf}
      </p>
      <Disclosure result={result} />
    </section>
  );
}

function ForecastView({ result }: { result: CompanyCapexSliceResult }) {
  const total = result.forecast.total;
  const first = total.points[0];
  const fourth = total.points[3];
  return (
    <section className="sheet company-capex-panel" aria-labelledby="company-capex-forecast-heading">
      <div className="sheet-heading">
        <div>
          <div className="section-kicker">COMPANY CAPEX / FORECAST</div>
          <h2 id="company-capex-forecast-heading">大厂 CapEx 未来 4 季</h2>
          <p>公司级 rolling-origin 模型分别预测后严格加总；历史回测不受情景参数影响。</p>
        </div>
        <div className="forecast-summary">
          <span>下一季度预测 · {first.period}</span>
          <strong>{money(first.predicted)}</strong>
          <small>{first.intervalCalibrated ? "90% OOS conformal" : "未校准 fallback"} {money(first.low)} — {money(first.high)}</small>
          <b>{pctChange(first.predicted, total.latestActual)} vs 最新实际</b>
        </div>
      </div>
      <section className="diagnostic-strip" aria-label="CapEx 预测准确性摘要">
        <div><span>最新实际</span><strong>{money(total.latestActual)}</strong><small>{result.forecast.commonPeriod}</small></div>
        <div><span>1Q 预测</span><strong>{money(first.predicted)}</strong><small>{first.period}</small></div>
        <div><span>4Q 预测</span><strong>{money(fourth.predicted)}</strong><small>{fourth.period}</small></div>
        <div><span>近期 RMSE</span><strong>{money(total.selection.recentRmse)}</strong><small>lag-1 {money(total.selection.lag1RecentRmse)}</small></div>
      </section>
      <div className="table-shell">
        <table className="research-table company-table">
          <caption className="sr-only">各公司 CapEx 未来四季度预测与回测准确性</caption>
          <thead>
            <tr><th>公司</th><th>最新实际</th><th>下一季度预测</th><th>4Q 预测</th><th>近期 RMSE</th><th>lag-1 RMSE</th><th>Champion</th></tr>
          </thead>
          <tbody>
            {result.forecast.companies.map((company) => (
              <tr key={company.id}>
                <td><strong>{company.name}</strong><small>{company.id.toUpperCase()}</small></td>
                <td>{money(company.latestActual)}</td>
                <td>{money(company.points[0].predicted)}</td>
                <td>{money(company.points[3].predicted)}</td>
                <td>{money(company.selection.recentRmse)}</td>
                <td>{money(company.selection.lag1RecentRmse)}</td>
                <td>{expertLabel(company.selectedExpert)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="data-caveat">
        未来区间随 horizon 按 √h 扩大；fallback 不作为已校准 90% 区间展示。
      </p>
      <Disclosure result={result} />
    </section>
  );
}

/** Embeddable content for the product's existing Monitor and Forecast tabs. */
export function CompanyCapexPanel({ result, mode }: CompanyCapexPanelProps) {
  return mode === "monitor" ? <MonitorView result={result} /> : <ForecastView result={result} />;
}
